/**
 * Pixi v8 stage — layered scene with async init.
 *
 * See `wp6-client-render.md` § "Pixi stage & placeholder rendering" and, for
 * the arena/map art, issue #172 + `art/style-bible.md` §7.
 *
 *   • `Application` init is ASYNC (`await app.init(...)`) — do not
 *     copy v7 tutorials that do it in the constructor.
 *   • Layers (Containers): `floor` (tiled iso floor texture, behind
 *     everything), `ground` (arena edge treatment + spawn markers — STATIC,
 *     drawn once; the collision-registered pillars are NOT here: they render
 *     as 3D columns inside the Three scene (#816), and since #829 that is the
 *     ONLY pillar path — there is no 2D pillar art left to suppress), `walls`
 *     (dynamic-terrain walls, #488 — RECONCILED every frame against the
 *     wire's per-tick `walls` array, unlike the STATIC `ground`), `entities`
 *     (y-sorted by world `y` each frame, #730), `fx` (projectiles + combat text).
 *     `floor` + `ground` nest under `worldBelow`; `walls` + `entities` +
 *     `fx` nest under `overlayAbove` — a grouping the 3D composite
 *     (#237/#243) renders as two separate Pixi passes so the Three
 *     character layer draws BETWEEN them (world-below → Three →
 *     overlay-above), instead of underneath the entire Pixi stage. When
 *     Three is inactive, Pixi's normal auto-render still walks `app.stage`
 *     (worldBelow then overlayAbove) in the same order as today —
 *     byte-for-byte unchanged (rip-out contract).
 *   • DPR: `resolution: devicePixelRatio, autoDensity: true`. In
 *     Playwright we force `deviceScaleFactor: 1` so baselines match.
 *   • Screen-space UI bars (hp/cast) reposition on resize — the
 *     stage exposes an `onResize` callback wire so `entityView`
 *     recomputes centering.
 *
 * ARENA ART (RIP-OUT CONTRACT, #172): the floor / edge textures are ADDITIVE
 * over a working procedural base. Each is loaded from bundled content assets
 * (`arenaAssets.ts`); when an asset is absent or fails to load, that layer falls
 * back to its procedural vector draw. Deleting those arena PNGs returns the
 * arena to exactly today's flat vector rendering — never breaks.
 *
 * PILLARS ARE THE ONE EXCEPTION (#829). They are 3D-EXCLUSIVE: the Three prop
 * layer owns them (`three3d/propModel.ts`), this module draws nothing for them,
 * and there is no 2D sprite/procedural fallback to fall back TO. A missing or
 * malformed `pillar.glb` therefore FAILS LOUDLY at load (see `mount.ts`) rather
 * than degrading — the alternative, silently invisible but still-colliding
 * pillars, is the worst failure mode this renderer can have.
 */

import { Application, Assets, Container, Graphics, type Texture } from "pixi.js";
import {
  ISO_SCALE_X,
  ISO_SCALE_Y,
  NO_OFFSET,
  computeArenaCenter,
  type ProjectionCenter,
  worldToScreen,
} from "./projection.js";
import type { ArenaPillar } from "./arena.js";
import { arenaTextureSrc } from "./arenaAssets.js";
import { KNOWN_SPRITE_CHAMPION_IDS, resolveChampionSheet } from "./championSprites.js";
import type { LoadedChampionSheet } from "./entityView.js";
import { ARENA_BORDER_COLOR, BG_COLOR, EDGE_WALL_COLOR, SPAWN_MARKER_COLOR } from "./colors.js";

/** Static arena / map geometry driven into the ground layer. */
export interface ArenaGeometry {
  readonly width: number;
  readonly height: number;
  /** #235: one array of spawn points PER TEAM (3 per side at MVP content),
   *  mirroring `PitMap.spawns`'s shape — NOT a flat point list. */
  readonly spawns: readonly (readonly { readonly x: number; readonly y: number }[])[];
  /** Collision pillars (`{ x, y, radius }` circles from `MAP_SCHEMA`). Handed
   *  to the Three prop layer as 3D columns (`mount.ts`'s `syncPillarProps`) —
   *  nothing in this module draws them. Empty for a bare arena. */
  readonly pillars: readonly ArenaPillar[];
  /** The content map's `slug`, when the geometry is sourced from a real map
   *  (`arenaCatalog.ts`) — the arena's map identity. `undefined` for
   *  synthetic/fixture arenas with no map identity (`FIXTURE_ARENA`,
   *  `FALLBACK_ARENA`). */
  readonly slug?: string;
}

/** Loaded arena art textures, or `null` per layer when no art is committed.
 *  No `pillar` entry since #829: pillars are 3D-exclusive, so this module never
 *  loads or draws the 2D pillar sprite (`arenaAssets.ts` still resolves the
 *  committed PNG for any other consumer). */
export interface ArenaTextures {
  readonly floor: Texture | null;
  readonly edge: Texture | null;
}

/** Bundle of Pixi objects the renderer owns. */
export interface Stage {
  readonly app: Application;
  readonly layers: {
    readonly floor: Container;
    readonly ground: Container;
    /** Dynamic-terrain walls (#488). UNLIKE `ground` (static map art, drawn
     *  once), this layer is reconciled every frame against the
     *  wire's per-tick `walls` array (spawn/expire), same discipline as
     *  `entities`/`fx`. Drawn UNDER `entities` so characters render on top
     *  of a placeholder wall obstacle. */
    readonly walls: Container;
    readonly entities: Container;
    readonly fx: Container;
    /** `floor` + `ground` — the world pass the 3D composite renders
     *  BEFORE the Three character layer (#243). */
    readonly worldBelow: Container;
    /** `walls` + `entities` + `fx` — the overlay pass the 3D composite
     *  renders AFTER the Three character layer (#243). */
    readonly overlayAbove: Container;
  };
  /** Current viewport transform (cover zoom + centering offset) — recomputed
   *  every frame from the live canvas size (#730). */
  center: ProjectionCenter;
  /** Static arena geometry (constant for a match). */
  readonly arena: ArenaGeometry;
  /** Arena art textures (each `null` → procedural fallback for that layer). */
  readonly textures: ArenaTextures;
  /** Loaded champion sprite sheets keyed by champion id (empty → procedural bodies). */
  readonly championSheets: ReadonlyMap<string, LoadedChampionSheet>;
  /** Detach listeners, destroy Pixi resources. */
  destroy: () => void;
}

/**
 * Load the three arena art textures. Each is resolved from bundled content
 * assets and loaded through Pixi's `Assets`; a missing source (no PNG committed)
 * or a load failure yields `null` so the renderer falls back procedurally — the
 * rip-out contract (#172). Never throws.
 */
async function loadArenaTextures(): Promise<ArenaTextures> {
  const load = async (kind: "floor" | "edge"): Promise<Texture | null> => {
    const src = arenaTextureSrc(kind);
    if (src === null) return null;
    try {
      return await Assets.load<Texture>(src);
    } catch {
      return null;
    }
  };
  const [floor, edge] = await Promise.all([load("floor"), load("edge")]);
  return { floor, edge };
}

/**
 * Load every known champion's sprite sheet into a Pixi texture (#223 Task 4).
 * A champion with no committed PNG/metadata — or a load failure — is skipped, so
 * its entities fall back to the procedural body (the rip-out contract). Returns
 * an empty map until sprite art is committed. Never throws.
 */
async function loadChampionSheets(): Promise<Map<string, LoadedChampionSheet>> {
  const sheets = new Map<string, LoadedChampionSheet>();
  await Promise.all(
    KNOWN_SPRITE_CHAMPION_IDS.map(async (championId) => {
      const resolved = resolveChampionSheet(championId);
      if (resolved === null) return;
      try {
        const texture = await Assets.load<Texture>(resolved.sheetUrl);
        sheets.set(championId, { texture, meta: resolved.meta });
      } catch {
        // Load failure → leave unset → procedural body for this champion.
      }
    }),
  );
  return sheets;
}

/**
 * Create and initialize a Pixi stage inside `container`.
 * Awaits `app.init` (v8 required) and the arena textures.
 */
export async function createStage(
  container: HTMLElement,
  arena: ArenaGeometry,
): Promise<Stage> {
  const app = new Application();
  await app.init({
    background: BG_COLOR,
    resizeTo: container,
    antialias: true,
    resolution: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    autoDensity: true,
  });

  container.appendChild(app.canvas);

  const textures = await loadArenaTextures();
  const championSheets = await loadChampionSheets();

  const floor = new Container();
  const ground = new Container();
  const walls = new Container();
  const entities = new Container();
  const fx = new Container();
  floor.label = "floor";
  ground.label = "ground";
  walls.label = "walls";
  entities.label = "entities";
  fx.label = "fx";

  // Draw order: floor (back) → ground → walls → entities → fx (front).
  // `worldBelow` and `overlayAbove` are pure grouping containers (#243) so
  // the 3D composite can render them as two separate Pixi passes with the
  // Three character layer sandwiched between; when Three is inactive,
  // walking `app.stage` renders both groups in this same order — unchanged
  // today. `walls` (#488) joins `overlayAbove` alongside `entities`/`fx`
  // (NOT `worldBelow` alongside the static `ground` art) because it is
  // reconciled every frame against per-tick wire state, not drawn once.
  const worldBelow = new Container();
  const overlayAbove = new Container();
  worldBelow.label = "worldBelow";
  overlayAbove.label = "overlayAbove";
  worldBelow.addChild(floor);
  worldBelow.addChild(ground);
  overlayAbove.addChild(walls);
  overlayAbove.addChild(entities);
  overlayAbove.addChild(fx);
  app.stage.addChild(worldBelow);
  app.stage.addChild(overlayAbove);

  const stage: Stage = {
    app,
    layers: { floor, ground, walls, entities, fx, worldBelow, overlayAbove },
    // Initial center: static arena fit. mount.ts overwrites this every
    // frame (follow camera). Use app.screen.* (CSS px) not renderer.*
    // (physical px) — see WP10 Traps (DPR).
    center: computeArenaCenter(app.screen.width, app.screen.height, arena.width, arena.height),
    arena,
    textures,
    championSheets,
    destroy: () => undefined, // patched below
  };

  // Draw statics ONCE at NO_OFFSET; the camera is applied by scaling +
  // translating the layer containers (below + per-frame in mount.ts), not by
  // baking the transform into vertex coords. No resize listener: the per-frame
  // camera recomputes from live canvas dimensions.
  drawArena(stage);

  // Apply the initial camera transform to all world layers so the
  // waiting-room view (arena before any snapshot) matches the static framing.
  applyCameraTransform(stage, stage.center);

  const destroy = (): void => {
    // v8: destroy the application (removes canvas, tears down ticker).
    //
    // BUG (black-screen regression, discovered after #353): `texture: true,
    // textureSource: true` tells Pixi to destroy the GPU resource behind
    // EVERY texture still attached to the display tree — including textures
    // resolved via `Assets.load()` (arena floor/pillar/edge PNGs, champion
    // sprite sheets), which are shared/cached at module scope, not owned by
    // this stage instance. React 18 StrictMode double-invokes this mount's
    // effect (mount → cleanup → mount) even in the production build (see
    // main.tsx); the in-flight FIRST mount's async `createStage()` can
    // resolve after its own cleanup already ran, so its `dispose()` reaches
    // here and destroys the shared `pillar.png` TextureSource. The SECOND
    // (real) mount then calls `Assets.load("pillar.png")` again, gets back
    // the SAME cached `Texture` object (Pixi caches by URL) wrapping a
    // dead GPU resource, and a later real frame render throws in Pixi's
    // `RenderTargetSystem.buildEnd` reading a null resource's `alphaMode` —
    // the entire canvas goes black (map/pillars/units never draw again).
    // This never surfaced before #353 (#331) because `pillars: []` meant no
    // `Sprite` ever actually USED the pillar texture, so a destroyed-but-
    // still-cached texture object was never rendered.
    //
    // Fix: never destroy Assets-managed texture sources here (matches the
    // Pixi warning this bug throws: "A TextureSource managed by Assets was
    // destroyed instead of unloaded! Use Assets.unload() instead."). This
    // stage never owns any instance-generated texture (no
    // `renderer.generateTexture`/`RenderTexture` anywhere in this module),
    // so there is nothing here that legitimately needs texture-level
    // teardown — only the canvas/renderer/ticker (`children: true` is still
    // safe: it destroys Pixi DISPLAY OBJECTS — Sprites/Graphics/Containers
    // — not the textures they reference).
    app.destroy(true, { children: true, texture: false, textureSource: false });
  };
  // Attach patched destroy.
  (stage as { destroy: () => void }).destroy = destroy;

  return stage;
}

/**
 * Apply a `ProjectionCenter` to every world layer — the ONE place the viewport
 * transform meets Pixi. Layer contents are drawn in NO_OFFSET (container)
 * space, so the container carries the whole transform: `zoom` as a uniform
 * scale about the projected world origin, `(cx, cy)` as the translation. That
 * composition is exactly `worldToScreen(x, y, center)`, which is what lets the
 * hit-test / cursor→world / HUD-overlay paths agree with what is drawn while
 * the draw calls themselves keep passing NO_OFFSET.
 *
 * Uniform scale on both axes: the projection's own `ISO_Y_COMPRESSION` squash
 * (`k = sin(camera elevation)` — 0.650647 = sin(40.59°) since #842, was 0.75)
 * already lives in the vertex coordinates, so zoom must not re-apply it.
 */
export function applyCameraTransform(stage: Stage, center: ProjectionCenter): void {
  for (const layer of [
    stage.layers.floor,
    stage.layers.ground,
    stage.layers.walls,
    stage.layers.entities,
    stage.layers.fx,
  ]) {
    layer.position.set(center.cx, center.cy);
    layer.scale.set(center.zoom);
  }
}

/**
 * Draw the static arena — floor (floor layer), then edge + spawn markers
 * (ground layer; pillars are 3D props, #829). Called ONCE from `createStage`.
 * Vertices are in NO_OFFSET (container) coordinates; the camera transform is
 * applied by scaling + translating the layer containers
 * (`applyCameraTransform`), not by baking it into these vertices — which is why
 * a zoom change costs nothing here (no redraw).
 *
 * Each art element is textured when its asset is present, else procedural —
 * deleting the assets returns the arena to today's flat vector look.
 */
export function drawArena(stage: Stage): void {
  stage.layers.floor.removeChildren();
  stage.layers.ground.removeChildren();

  drawFloor(stage.layers.floor, stage.arena, stage.textures.floor);
  drawEdge(stage.layers.ground, stage.arena, stage.textures.edge);
  drawSpawns(stage.layers.ground, stage.arena);
  // #829: no pillar draw here. Pillars are 3D-exclusive — `mount.ts` pushes
  // `arena.pillars` into the Three prop layer, which owns them for good.
}

/**
 * #702 follow-up: swap `stage.arena` to the ACTUAL resolved map (the wire
 * `matchCreated`/`matchPhase` `mapSlug`, via `arenaForSlug` — see
 * `arenaCatalog.ts`) and redraw the static floor/edge/spawn art against it
 * (the new map's pillars are re-pushed to the Three prop layer by `mount.ts`'s
 * own `setArena`). Needed because `stage.arena` is set ONCE at `createStage` —
 * `MatchScreen.tsx`'s renderer mount can resolve BEFORE the match's first
 * `matchCreated`/`matchPhase` arrives (most likely for a JOINING teammate,
 * whose fresh WS connect's first message races the renderer's own async
 * asset load), in which case the mount captured the `null`-slug fallback
 * (`LIVE_ARENA`/`bare`) and — absent this function — would never self-
 * correct to the map the match is ACTUALLY running on.
 *
 * `stage.arena` is `readonly` on the type (constant-for-a-match is the
 * common case) — mutated here via the same patch-cast `createStage` already
 * uses for `stage.destroy`. A no-op re-assign when `next` is the same arena
 * object `stage.arena` already holds (the overwhelmingly common case: the
 * match's first `matchCreated` confirms the SAME map the mount already
 * resolved) still safely re-runs `drawArena` — cheap (one static redraw, no
 * allocation churn worth guarding against) and avoids a fragile identity/
 * deep-equality check that could itself hide a real mismatch.
 *
 * Camera re-centering needs NO extra call here: `mount.ts`'s per-frame loop
 * already recomputes `stage.center` from `stage.arena.width`/`height` every
 * frame (follow AND static camera modes both read `stage.arena` live, not a
 * value captured at mount) — so the very next rendered frame after this call
 * uses the corrected dimensions automatically.
 */
export function setArena(stage: Stage, next: ArenaGeometry): void {
  (stage as { arena: ArenaGeometry }).arena = next;
  drawArena(stage);
}

/**
 * The four arena corners in NO_OFFSET (container) space, CW from the world
 * origin. Since #730's axis-aligned basis these form a screen-axis-aligned
 * RECTANGLE (they formed a diamond while the basis was rotated 45° — hence the
 * old `diamondPoints` name, renamed here so the next reader isn't misled).
 * Still built by projecting the four map corners rather than assuming the
 * shape, so this stays correct if the basis ever changes again.
 */
function arenaCorners(arena: ArenaGeometry): number[] {
  const { width: w, height: h } = arena;
  const c00 = worldToScreen(0, 0, NO_OFFSET);
  const cW0 = worldToScreen(w, 0, NO_OFFSET);
  const cWH = worldToScreen(w, h, NO_OFFSET);
  const c0H = worldToScreen(0, h, NO_OFFSET);
  return [c00.sx, c00.sy, cW0.sx, cW0.sy, cWH.sx, cWH.sy, c0H.sx, c0H.sy];
}

/**
 * Floor layer: a tiled stone texture filling the arena rectangle (#730 — the
 * projected playable area is a rectangle now, not a diamond). Procedural
 * fallback (no texture) draws NOTHING — the flat vector arena has no floor
 * fill (the background clear color shows through), per the rip-out contract.
 */
function drawFloor(floor: Container, arena: ArenaGeometry, texture: Texture | null): void {
  if (texture === null) return;
  // Repeat-wrap + global texture space tiles the stone across the whole floor
  // rather than stretching a single copy to fit.
  texture.source.addressMode = "repeat";
  const g = new Graphics();
  g.poly(arenaCorners(arena)).fill({ texture, textureSpace: "global" });
  floor.addChild(g);
}

/**
 * Edge treatment: a low stone wall band hugging the arena rim (textured), else
 * the procedural 1 px border (today's look). Replaces the flat border when art
 * is present (bible §7 "no ambiguity about playable space").
 *
 * #730: with the map edge now coinciding with the screen edge from every camera
 * position, this band is usually off-screen or clipped — it marks the boundary
 * for the corner/edge cases where the camera cannot scroll further, which is
 * exactly when a player needs to read "this is the end of the playable area".
 */
function drawEdge(ground: Container, arena: ArenaGeometry, texture: Texture | null): void {
  const pts = arenaCorners(arena);
  if (texture === null) {
    // Procedural fallback: the original 1 px border outline.
    const border = new Graphics();
    border.poly(pts).stroke({ width: 1, color: ARENA_BORDER_COLOR });
    ground.addChild(border);
    return;
  }
  texture.source.addressMode = "repeat";
  const wall = new Graphics();
  // A thick textured band on the boundary (a low wall / drop-off), backed by a
  // quiet stone fill so the rim reads as solid even before the texture loads.
  wall
    .poly(pts)
    .stroke({ width: EDGE_WALL_WIDTH_PX, color: EDGE_WALL_COLOR, alignment: 0.5 })
    .poly(pts)
    .stroke({ width: EDGE_WALL_WIDTH_PX, texture, textureSpace: "global", alignment: 0.5 });
  ground.addChild(wall);
}

/** Screen-space thickness of the textured edge wall band, in px. */
const EDGE_WALL_WIDTH_PX = 8;

/** Spawn markers — small screen-space diamonds (unchanged from the base draw).
 *  #235: `arena.spawns` is now one array of points PER TEAM — draw every
 *  point across every team (was a flat list pre-#235; visually identical
 *  for content with 1 point/team, now draws all 3/side). */
function drawSpawns(ground: Container, arena: ArenaGeometry): void {
  for (const teamSpawns of arena.spawns) {
    for (const s of teamSpawns) {
      const p = worldToScreen(s.x, s.y, NO_OFFSET);
      const d = new Graphics();
      const rx = 12;
      const ry = 6;
      d.moveTo(p.sx, p.sy - ry)
        .lineTo(p.sx + rx, p.sy)
        .lineTo(p.sx, p.sy + ry)
        .lineTo(p.sx - rx, p.sy)
        .closePath()
        .stroke({ width: 1, color: SPAWN_MARKER_COLOR });
      ground.addChild(d);
    }
  }
}

/** Iso constants re-exported for stage-adjacent modules that don't
 *  want a second import path. */
export const STAGE_ISO = { ISO_SCALE_X, ISO_SCALE_Y } as const;
